import { Checkbox, ErrorText, FileUpload, FormGroup, HintText, InputField, LabelText, Radio, Select, TextArea } from "govuk-react";
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import Autocomplete, { AccessibleAutocompleteProps, AutocompleteTemplates, SourceFunction } from "accessible-autocomplete/react";
import "accessible-autocomplete/dist/accessible-autocomplete.min.css";

export interface GovFieldProps {
    contentType: GovFieldContentType;
    label?: string;
    inputName?: string;
    value?: any;
    onChange?: (e: any, value: any) => any | Promise<any>;
    onSelected?: (value: any) => void;
    errorMsg?: string;
    radioValue?: string;
    hint?: React.ReactNode;
    placeholder?: string;
    required?: boolean;
    /**
     *
     * @param option
     * @returns string which can be an HTML string
     */
    customRenderOption?: (option: string) => string;
    /**
     * Allow multiple files to be uploaded
     * @default is false
     */
    multipleFiles?: boolean;
    disabled?: boolean;
    step?: number;
    min?: number;
    max?: number;
    autocompleteProps?: Partial<AccessibleAutocompleteProps>;
    showAutocompleteValueAfterSelection?: boolean;
    options?: string[];
}

export const GovField = memo((props: GovFieldProps) => {
    switch (props.contentType) {
        case "CHECKBOX":
            return <GovCheckbox {...props} />;
        case "TEXT": {
            return <GovTextField {...props} />;
        }
        case "NUMBER": {
            return <GovNumberField {...props} />;
        }
        case "TEXTAREA":
            return <GovTextAreaField {...props} />;
        case "RADIO":
            return <GovRadioField {...props} />;
        case "PICKLIST":
            return <GovPicklistField {...props} />;
        case "AUTOCOMPLETE":
            return <GovAutocompleteField {...props} />;
        case "FILE_UPLOAD":
            return <GovFileUploadField {...props} />;
        default:
            throw Error(`No content type found for the field. Provided type - ${props.contentType} and label - ${props.label}`);
    }
});

const GovCheckbox: React.FC<GovFieldProps> = memo((props) => {
    const { label, value, onChange, inputName, required } = props;
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange && onChange(e, e.target.checked);
    };
    const labelClassName = required ? "required" : "";
    return (
        <Checkbox required={required} checked={value} onChange={handleOnChange} name={inputName} style={{ marginBottom: "1rem" }}>
            <span className={labelClassName}>{label}</span>
        </Checkbox>
    );
});

const GovNumberField: React.FC<GovFieldProps> = memo((props) => {
    const { label, value, onChange, errorMsg, inputName, required, disabled, step, min, max } = props;
    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if ((typeof max === "number" && Number(event.target.value) > max) || (typeof min === "number" && Number(event.target.value) < min)) {
            return;
        }
        onChange && onChange(event, event.target.value);
    };
    const labelClassName = required ? "required" : "";
    return (
        <InputField
            hint={getMinMaxHint()}
            input={{ disabled, required, value, name: inputName, onChange: handleOnChange, style: { marginBottom: "1rem" }, type: "number", step, min, max }}
            meta={{ error: errorMsg, touched: true }}
        >
            <span className={labelClassName}>{label}</span>
        </InputField>
    );

    function getMinMaxHint() {
        let hint = "";
        if (min !== undefined) {
            hint += `Min: ${min}`;
        }
        if (max !== undefined) {
            hint += hint ? `, Max: ${max}` : `Max: ${max}`;
        }
        return hint;
    }
});
const GovTextField: React.FC<GovFieldProps> = memo((props) => {
    const { label, value, onChange, errorMsg, inputName, required, disabled } = props;
    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange && onChange(event, event.target.value);
    };
    const labelClassName = required ? "required" : "";
    return (
        <InputField input={{ disabled, required, value, name: inputName, onChange: handleOnChange, style: { marginBottom: "1rem" } }} meta={{ error: errorMsg, touched: true }}>
            <span className={labelClassName}>{label}</span>
        </InputField>
    );
});
const GovPicklistField: React.FC<GovFieldProps> = memo((props) => {
    const { label, value, onChange, errorMsg, inputName, required, disabled, options } = props;
    const handleOnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        onChange && onChange(event, event.target.value);
    };
    const labelClassName = required ? "required" : "";

    return (
        <FormGroup>
            <LabelText className={labelClassName}>{label}</LabelText>
            <Select label="" input={{ name: inputName, onChange: handleOnChange, defaultValue: "" }} meta={{ error: errorMsg, touched: true }}>
                <option value="" disabled selected>
                    Select an option
                </option>
                {options?.map((option: string) => {
                    return (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    );
                })}
            </Select>
        </FormGroup>
    );
});

const GovTextAreaField: React.FC<GovFieldProps> = memo((props) => {
    const { label, value, onChange, errorMsg, inputName, required } = props;
    const handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange && onChange(event, event.target.value);
    };
    const labelClassName = required ? "required" : "";
    return (
        <TextArea
            input={{ required, value, name: inputName, onChange: handleOnChange, style: { marginBottom: "1rem", maxWidth: "100%" } }}
            meta={{ error: errorMsg, touched: true }}
        >
            <span className={labelClassName}>{label}</span>
        </TextArea>
    );
});
const GovFileUploadField: React.FC<GovFieldProps> = memo((props) => {
    const { label, onChange, errorMsg, inputName, required, multipleFiles } = props;
    const uploadRef = useRef<HTMLInputElement>(null);
    const labelClassName = required ? "required" : "";
    useEffect(() => {
        if (multipleFiles && uploadRef.current) {
            uploadRef.current.multiple = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multipleFiles]);

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange && onChange(event, event.target.files);
    };
    return (
        <div style={{ marginBottom: "1rem" }}>
            <FileUpload name={inputName} onChange={handleOnChange} ref={uploadRef} meta={{ error: errorMsg, touched: true }}>
                <span className={labelClassName}>{label}</span>
            </FileUpload>
        </div>
    );
});
const GovRadioField: React.FC<GovFieldProps> = memo((props) => {
    const { label, value, onChange, errorMsg, inputName, radioValue, required } = props;
    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange && onChange(event, event.target.value);
    };
    return (
        <Radio required={required} name={inputName} value={radioValue} checked={value === radioValue} onChange={handleOnChange} style={{ marginBottom: "1rem" }}>
            {label}
        </Radio>
    );
});
const GovAutocompleteField: React.FC<GovFieldProps> = memo((props) => {
    const { label, onChange, onSelected, customRenderOption, hint, placeholder, required, errorMsg, autocompleteProps, showAutocompleteValueAfterSelection } = props;
    const labelWithDashes = label?.replace(/\s/g, "-").toLowerCase() || "(none)";
    const autocompleteId = `autocomplete-${labelWithDashes}`;
    const onConfirm = (confirmedValue?: any) => {
        if (!onSelected) {
            return;
        }
        onSelected(confirmedValue);
    };
    const renderOption = useCallback((option: any | null) => {
        return `<li key=${option?.id}>
                ${option?.label}
            </li>`;
    }, []);
    const parseSelectedOption = useCallback((option: any) => (showAutocompleteValueAfterSelection ? option?.label : ""), [showAutocompleteValueAfterSelection]);
    const sourceFunction: SourceFunction = (query, populateResults) => {
        let newOptions: any[] = [];
        if (!onChange) {
            return newOptions;
        }

        const result = onChange({}, query);
        if (result instanceof Promise) {
            result.then((options: any[]) => {
                populateResults(options);
                newOptions = [...options];
            });
        } else {
            populateResults(result);
            newOptions = [...result];
        }
        return newOptions;
    };

    const templates: AutocompleteTemplates = useMemo(() => {
        return { suggestion: customRenderOption ? customRenderOption : renderOption, inputValue: parseSelectedOption } as AutocompleteTemplates;
    }, [customRenderOption, parseSelectedOption, renderOption]);
    const labelClassName = required ? "required" : "";

    return (
        <FormGroup error={!!errorMsg}>
            <LabelText className={labelClassName}>{label}</LabelText>
            <ErrorText>{errorMsg}</ErrorText>
            <HintText>{hint}</HintText>
            <Autocomplete
                {...autocompleteProps}
                id={autocompleteId}
                source={sourceFunction}
                onConfirm={onConfirm}
                templates={templates}
                placeholder={placeholder}
                required={required}
                name={autocompleteId}
            />
        </FormGroup>
    );
});

export type GovFieldContentType = "TEXT" | "CHECKBOX" | "TEXTAREA" | "RADIO" | "AUTOCOMPLETE" | "FILE_UPLOAD" | "NUMBER" | "PICKLIST";
